import { HttpError, useOne, useTranslation } from "@refinedev/core";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import Textarea from "components/textarea";
import {
  IAdditionalSetupForm,
  IEventIdsItem,
  ISimpleEventDetail,
  PrivateFlowVerifyType,
  UserType,
} from "interfaces";
import { get } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { PRIVATE_FLOW_VERIFY_TYPES } from "utills/constants";
import { canAccessByType } from "utills/helpers";

export const PrivateFlow = ({
  isEdit,
}: {
  isEdit: boolean;
  eventDetailData: ISimpleEventDetail;
}) => {
  const { data, isFetching: isFetchingEvents } = useOne<
    IEventIdsItem[],
    HttpError
  >({
    resource: "content/dropdown/events?v=2",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!isEdit,
    } as any,
  });

  const { translate } = useTranslation();
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<IAdditionalSetupForm>(); // retrieve all hook methods

  const eventIds = (data?.data ?? []).map((event: IEventIdsItem) => ({
    value: event.id,
    label: event.name_en,
  }));

  return isEdit ? (
    <div className="box-container p-4 sm:p-6">
      <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
        <div className="col-span-full">
          <Label
            required
            id="private_flow.verify_type"
            name={translate(
              "eventSetup.additionalSetup.privateFlow.verifyType"
            )}
          />
          <Controller
            control={control}
            name="private_flow.verify_type"
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <Select
                  key="private_flow.verify_type"
                  inputRef={ref}
                  options={PRIVATE_FLOW_VERIFY_TYPES}
                  id="private_flow.verify_type"
                  placeholder={translate(
                    "eventSetup.additionalSetup.privateFlow.selectType"
                  )}
                  defaultValue={
                    PRIVATE_FLOW_VERIFY_TYPES.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={PRIVATE_FLOW_VERIFY_TYPES.find(
                    (selected: { label: string; value: any }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: any }) => {
                    onChange(selected?.value);
                  }}
                  error={get(errors, `private_flow.verify_type`, "")}
                />
              );
            }}
          />
          {get(errors, `private_flow.verify_type`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `private_flow.verify_type.message`, "")}
              </span>
            </p>
          ) : null}
        </div>
        {watch("private_flow.verify_type") ===
        PrivateFlowVerifyType.DOMAIN_EMAIL ? (
          <div className="col-span-full">
            <Label
              required
              id="private_flow.email_domain"
              name={translate(
                "eventSetup.additionalSetup.privateFlow.inputEmailDomain"
              )}
            />
            <Controller
              control={control}
              name="private_flow.email_domain"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Textarea
                    id="private_flow.email_domain"
                    placeholder={translate(
                      "eventSetup.additionalSetup.privateFlow.inputEmailDomain"
                    )}
                    error={get(errors, `private_flow.email_domain`, "")}
                    onChange={onChange}
                    value={value}
                  />
                );
              }}
            />
            <p className="text-gray-400 text-sm mt-1">
              {translate(
                "eventSetup.additionalSetup.privateFlow.inputEmailDomainNote"
              )}
            </p>
            {errors["private_flow"]?.email_domain ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `private_flow.email_domain.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
        ) : (
          <></>
        )}
        {canAccessByType(UserType.ADMIN) && (
          <>
            <div className="col-span-full">
              <Controller
                control={control}
                name="private_flow.sync_enabled"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    checked={value}
                    onChange={(isCheck) => {
                      onChange(isCheck);
                    }}
                    label="Sync Order Code"
                  />
                )}
              />
            </div>
            {watch("private_flow.sync_enabled") && (
              <>
                <div className="sm:col-span-3">
                  <Label
                    required
                    id="private_flow.usage_count"
                    name={translate(
                      "eventSetup.additionalSetup.privateFlow.usageCount"
                    )}
                  />
                  <Input
                    id="private_flow.usage_count"
                    type="number"
                    error={get(errors, "private_flow.usage_count")}
                    placeholder={translate(
                      "eventSetup.additionalSetup.privateFlow.usageCount"
                    )}
                    {...register("private_flow.usage_count")}
                    min={0}
                  />
                  {get(errors, `private_flow.usage_count`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `private_flow.usage_count.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
                <div className="sm:col-span-3">
                  <Label
                    id="private_flow.passcode_source_event_ids"
                    name="Events"
                    required
                  />
                  <Controller
                    control={control}
                    name="private_flow.passcode_source_event_ids"
                    defaultValue={[]}
                    render={({ field: { onChange, value = [], ref } }) => (
                      <Select
                        isMulti
                        inputRef={ref}
                        options={eventIds}
                        id="private_flow.passcode_source_event_ids"
                        placeholder="Select Event"
                        value={value.map((id: string) => {
                          return eventIds.find((item) => item.value === id);
                        })}
                        onChange={(
                          selected: { label: string; value: string }[]
                        ) => onChange(selected.map((item) => `${item.value}`))}
                        error={
                          errors["private_flow"]?.passcode_source_event_ids
                        }
                        isLoading={isFetchingEvents}
                      />
                    )}
                  />
                  {errors["private_flow"]?.passcode_source_event_ids ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {
                          errors["private_flow"]?.passcode_source_event_ids
                            ?.message
                        }
                      </span>
                    </p>
                  ) : null}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  ) : (
    <></>
  );
};
